import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import { authService } from "@/services/authService";
import logo from "@/assets/images/logo.png";

export function ForgotPassword() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Call the API to request password reset
        await authService.requestPasswordReset(values.email);
        
        toast.success("Reset Link Sent", {
          description: "We have e-mailed your password reset link!",
          duration: 4000,
          position: "top-right",
        });
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } catch (error) {
        console.error("Password Reset Request Error:", error);
        toast.error("Failed to send reset link", {
          description: error.response?.data?.message || "Something went wrong. Please try again.",
          duration: 4000,
          position: "top-right",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <>
      <Toaster
        richColors
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
          duration: 3000,
        }}
      />
      <div className="min-h-screen flex items-center justify-center flex-col" style={{ backgroundColor: '#F1EBDB' }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg"
        >
          <div>
            <h2 className="text-3xl font-bold text-center text-black">Forgot Password</h2>
            <p className="mt-2 text-sm text-[#F16745] text-center mb-8">
              Enter your email to receive a password reset link
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="rounded-md">
              <div className="mb-4">
                <label htmlFor="email" className="block font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-2  text-red-600">{formik.errors.email}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#F16745] hover:bg-[#F16745]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 hover:scale-[1.02]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  "Send Reset Link"
                )}
              </motion.button>
              
              <motion.button
                type="button"
                onClick={() => navigate("/login")}
                className="text-sm text-center text-[#F16745] hover:text-[#F16745]/90 focus:outline-none"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Back to Login
              </motion.button>
            </div>
          </form>
        </motion.div>
        <div className="mt-8 mb-4">
          <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto" />
        </div>
      </div>
    </>
  );
}